<template>
  <header
    class="col-span-full flex items-center bg-secondary h-16 shrink-0 border-b border-tertiary"
  >
    <!-- Left Section: Logo / Workspace -->
    <div
      class="flex h-full w-16 shrink-0 items-center gap-3 border-r border-tertiary px-2 lg:w-70 lg:px-4"
    >
      <CodeLabIcon class="h-auto w-full max-w-18" />
    </div>

    <!-- Middle Section: IDE Controls -->
    <div class="flex items-center h-full shrink-0">
      <!-- File Operations -->
      <div class="flex h-full items-center gap-1 border-r border-tertiary px-1 lg:px-4">
        <!-- Save -->
        <div class="relative flex items-center gap-0" ref="saveDropdownRef">
          <ToolbarButton
            title="Save"
            data-testid="topbar-save"
            :path="mdiContentSave"
            @click="$emit('save')"
          />
          <ToolbarButton
            title="Versions"
            :path="mdiChevronDown"
            size="20"
            @click="showSaveMenu = !showSaveMenu"
          />

          <!-- Dropdown Options -->
          <div
            v-if="showSaveMenu"
            class="absolute top-full left-0 mt-1 bg-secondary border border-tertiary rounded-standard shadow-lg py-1 z-50 min-w-37.5"
          >
            <button
              class="w-full text-left px-4 py-2 text-sm text-text-main hover:bg-primary transition-colors"
              @click="handleSaveMenu('saveAs')"
            >
              Speichern unter...
            </button>
            <button
              class="w-full text-left px-4 py-2 text-sm text-text-main hover:bg-primary transition-colors"
              @click="handleSaveMenu('download')"
            >
              Als ZIP herunterladen
            </button>
          </div>
        </div>

        <!-- Undo -->
        <ToolbarButton title="Undo" :path="mdiArrowULeftTopBold" @click="$emit('undo')" />

        <!-- Redo -->
        <ToolbarButton title="Redo" :path="mdiArrowURightTopBold" @click="$emit('redo')" />

        <!-- Reset -->
        <ToolbarButton title="Reset" :path="mdiBackupRestore" @click="$emit('reset')" />
      </div>

      <!-- Execution Controls -->
      <div class="flex h-full items-center gap-1 border-r border-tertiary px-1 lg:gap-2 lg:px-4">
        <!-- Run/Pause Button -->
        <ToolbarButton
          title="Run / Pause"
          :path="runState === 'running' ? mdiPause : mdiPlay"
          @click="toggleRun"
        />

        <!-- Stop Button -->
        <ToolbarButton
          title="Stop"
          :path="mdiStop"
          size="65"
          :class="runState ? 'text-accent' : 'text-text-main opacity-30'"
          @click="$emit('update:runState', 'stopped' as const)"
        />
      </div>
    </div>

    <!-- Flexible Space -->
    <div class="flex-1" />

    <!-- Right Section: Speed slider, Fullscreen, Dark Mode, Profile -->
    <div class="flex items-center h-full shrink-0">
      <!-- Right Side Controls -->
      <div
        class="flex h-full items-center gap-1 border-l border-tertiary px-1 md:gap-2 md:px-2 lg:gap-6 lg:px-6"
      >
        <!-- Expandable Debug & Speed Slider -->
        <div class="relative flex shrink-0 items-center overflow-visible">
          <Transition
            enter-active-class="transition-opacity duration-180 ease-in-out"
            enter-from-class="opacity-0 -translate-y-1 scale-[0.99]"
            enter-to-class="opacity-100 translate-y-0 scale-100"
            leave-active-class="transition-opacity duration-180 ease-in-out"
            leave-from-class="opacity-100 translate-y-0 scale-100"
            leave-to-class="opacity-0 -translate-y-1 scale-[0.99]"
          >
            <div v-if="showSpeedSlider" class="debug-panel">
              <div
                class="debug-step-controls mr-2 flex shrink-0 items-center gap-1 border-r border-tertiary pr-2"
              >
                <ToolbarButton
                  title="Step Into"
                  :path="mdiDebugStepInto"
                  @click="$emit('step-into')"
                />
                <ToolbarButton
                  title="Step Over"
                  :path="mdiDebugStepOver"
                  @click="$emit('step-over')"
                />
                <ToolbarButton
                  title="Step Out"
                  :path="mdiDebugStepOut"
                  @click="$emit('step-out')"
                />
              </div>

              <div
                class="speed-control relative ml-2 flex w-30 items-center"
                @mouseenter="showSpeedTooltip = true"
                @mouseleave="showSpeedTooltip = false"
              >
                <input
                  :value="speedVal"
                  type="range"
                  min="0"
                  max="100"
                  class="speed-slider h-6 w-full"
                  :style="{ '--color-primary': `${speedVal}%` }"
                  @input="updateSpeed"
                  @mousedown="isDraggingSpeed = true"
                  @mouseup="isDraggingSpeed = false"
                  @touchstart="isDraggingSpeed = true"
                  @touchend="isDraggingSpeed = false"
                />
                <div
                  v-show="showSpeedTooltip || isDraggingSpeed"
                  class="absolute top-full mt-2 bg-(--tooltip-bg,#383838) border border-(--tooltip-border,#9d9d9d) text-(--tooltip-text,#e0e0e0) px-2 py-1 text-sm whitespace-nowrap z-50 pointer-events-none"
                  :style="{ left: `${speedVal}%`, transform: 'translateX(-50%)' }"
                >
                  {{ formattedSpeed }}
                </div>
              </div>
            </div>
          </Transition>

          <span class="debug-toggle">
            <ToolbarButton
              title="Geschwindigkeit & Debug"
              :path="mdiSpeedometer"
              @click="toggleSpeedSlider(true)"
            />
          </span>
        </div>

        <!-- Dark Mode Toggle (Pill) -->
        <div
          class="relative inline-flex h-8.75 w-18 shrink-0 cursor-pointer items-center rounded-full bg-primary p-0.5 transition-colors duration-200"
          :title="theme === 'dark' ? 'Helles Design' : 'Dunkles Design'"
          @click="$emit('toggle-theme')"
        >
          <span
            class="pointer-events-none flex h-8.75 w-8.75 items-center justify-center rounded-full bg-text-main text-primary shadow-sm transition-transform duration-200 ease-in-out"
            :class="theme === 'dark' ? 'translate-x-9' : 'translate-x-0'"
          >
            <MdiIcon :path="theme === 'dark' ? mdiWeatherNight : mdiWhiteBalanceSunny" size="22" />
          </span>
        </div>

        <!-- Profile Avatar -->
        <ToolbarButton title="Profil" :path="mdiAccountCircle" @click="$emit('open-profile')" />
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watchEffect } from 'vue'
import ToolbarButton from './ToolbarButton.vue'
import MdiIcon from './MdiIcon.vue'
import {
  mdiContentSave,
  mdiChevronDown,
  mdiArrowULeftTopBold,
  mdiArrowURightTopBold,
  mdiBackupRestore,
  mdiPlay,
  mdiPause,
  mdiStop,
  mdiDebugStepInto,
  mdiDebugStepOver,
  mdiDebugStepOut,
  mdiSpeedometer,
  mdiWhiteBalanceSunny,
  mdiWeatherNight,
  mdiAccountCircle,
} from '@mdi/js'
import CodeLabIcon from '@/components/CodeLabLogoFull.vue'

const props = defineProps<{
  runState: 'stopped' | 'running' | 'paused'
  speedVal: number
  theme: 'light' | 'dark'
  isDebugging?: boolean
}>()

const showSpeedSlider = ref(props.isDebugging ?? false)
const showSaveMenu = ref(false)
const saveDropdownRef = ref<HTMLElement | null>(null)
const showSpeedTooltip = ref(false)
const isDraggingSpeed = ref(false)

// sync local panel visibility with external controlled prop
watchEffect(() => {
  showSpeedSlider.value = props.isDebugging ?? false
})

const emit = defineEmits<{
  (e: 'update:runState', val: 'stopped' | 'running' | 'paused'): void
  (e: 'update:speedVal', val: number): void
  (e: 'toggle-theme'): void
  (e: 'open-profile'): void
  (e: 'step-into'): void
  (e: 'step-over'): void
  (e: 'step-out'): void
  (e: 'toggle-debug-panel', val: boolean): void
  (e: 'toggle-debug-panel-preserve-sidebars', val: boolean): void
  (e: 'undo'): void
  (e: 'redo'): void
  (e: 'reset'): void
  (e: 'save'): void
}>()

const formattedSpeed = computed(() => {
  const intervalBorders = [1, 10, 100, 1e3, 5e4, 1e6, 5e7]
  const val = props.speedVal
  const xMax = 100
  const intervalDelta = xMax / (intervalBorders.length - 1)

  let intervalIndex = Math.floor(val / intervalDelta)
  if (intervalIndex === intervalBorders.length - 1) intervalIndex--

  const factorInsideInterval = (val - intervalIndex * intervalDelta) / intervalDelta
  const intervalMin = intervalBorders[intervalIndex]
  const intervalMax = intervalBorders[intervalIndex + 1]

  let speed = intervalMin + (intervalMax - intervalMin) * factorInsideInterval

  if (speed >= intervalBorders[intervalBorders.length - 1] - 10) {
    return 'maximum speed'
  }

  const speedString =
    speed < 1e6 ? Math.trunc(speed).toString() : `${(Math.trunc(speed / 1e3) * 1e3) / 1e6} million`

  return `${speedString} steps/s`
})

function toggleRun() {
  if (props.runState === 'running') {
    emit('update:runState', 'paused')
  } else {
    emit('update:runState', 'running')
  }
}

function updateSpeed(event: Event) {
  const target = event.target as HTMLInputElement
  emit('update:speedVal', parseInt(target.value, 10))
}

function toggleSpeedSlider(preserve = false) {
  showSpeedSlider.value = !showSpeedSlider.value
  if (preserve) {
    emit('toggle-debug-panel-preserve-sidebars', showSpeedSlider.value)
  } else {
    emit('toggle-debug-panel', showSpeedSlider.value)
  }
}

function handleSaveMenu(action: string) {
  showSaveMenu.value = false
  if (action === 'saveAs') {
    // Implement saveAs logic
    console.log('Save As...')
  } else if (action === 'download') {
    // Implement download logic
    console.log('Download ZIP...')
  }
}

function handleClickOutside(event: MouseEvent) {
  if (
    showSaveMenu.value &&
    saveDropdownRef.value &&
    !saveDropdownRef.value.contains(event.target as Node)
  ) {
    showSaveMenu.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.speed-slider {
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  cursor: pointer;
}

.speed-slider::-webkit-slider-runnable-track {
  height: 8px;
  background: linear-gradient(
    to right,
    var(--color-accent) var(--color-primary, 50%),
    var(--color-text-main) var(--color-primary, 50%)
  );
  border-radius: 0;
}

.speed-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 12px;
  height: 24px;
  background: var(--color-accent);
  border-radius: 0;
  margin-top: -8px;
}

.speed-slider::-moz-range-track {
  height: 8px;
  background: linear-gradient(
    to right,
    var(--color-accent) var(--color-primary, 50%),
    var(--color-text-main) var(--color-primary, 50%)
  );
  border: 0;
  border-radius: 0;
}

.speed-slider::-moz-range-thumb {
  width: 12px;
  height: 24px;
  background: var(--color-accent);
  border: 0;
  border-radius: 0;
}

.debug-panel {
  display: contents;
}

.debug-step-controls {
  order: 1;
}

.debug-toggle {
  order: 2;
}

.speed-control {
  order: 3;
}

@media (max-width: 1499px) {
  .debug-panel {
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    z-index: 50;
    display: flex;
    width: 176px;
    flex-direction: column;
    gap: 8px;
    border: 1px solid var(--color-tertiary);
    background: var(--color-secondary);
    padding: 8px;
  }

  .debug-step-controls {
    width: 100%;
    justify-content: space-between;
    margin: 0;
    border-right: 0;
    border-bottom: 1px solid var(--color-tertiary);
    padding: 0 0 8px;
  }

  .speed-control {
    width: 100%;
    margin: 0;
  }
}
</style>
